import Konva from 'konva';
import { Vector2d } from 'konva/lib/types';
import Decimal from 'decimal.js';

import { generateRoomPartId, simpleClone } from './utils';
import { FourCorners, FourCornersDec, GridSize, SectorRings, Vector2dDecimal } from '@/types/planner';

import { DeviceLike, LengthUnit, Measurement, MeasurementType, UnitOfMeasurement } from '@/types/helpers';

import { Corner, Wall, Position, Sector, OffsetTransformProperties } from '@/api/airchef/Api';

import { InvalidStateError } from './errors';
import { measurementCalc, convertMeasurement, createMeasurement } from '@/common/measurements';

Decimal.set({
  rounding: Decimal.ROUND_HALF_CEIL,
});

export function getMousePosFromScreen(
  stage: Konva.Stage,
  snapTo?: boolean,
  grid?: GridSize,
  overrideCoords?: Vector2d,
): Vector2d {
  const pointer = overrideCoords ?? stage.getPointerPosition();

  if (!pointer) {
    return { x: -1, y: -1 };
  }

  const scale = stage.scaleX();

  const pos = {
    x: (pointer.x - stage.x()) / scale,
    y: (pointer.y - stage.y()) / scale,
  };

  if (!snapTo || !grid) {
    return pos;
  }

  return snapToGrid(pos, grid);
}

export const transformerSnapAngles = new Array(360 / 15 + 1).fill(null).map((_v, i) => i * 15);

export function snapToGrid(
  pos: Vector2dDecimal,
  grid: GridSize,
  wall?: [Vector2dDecimal, Vector2dDecimal] | null,
): Vector2dDecimal;
export function snapToGrid(pos: Vector2d, grid: GridSize, wall?: [Vector2d, Vector2d] | null): Vector2d;
export function snapToGrid(
  pos: Vector2d | Vector2dDecimal,
  grid: GridSize,
  wall?: [Vector2d, Vector2d] | [Vector2dDecimal, Vector2dDecimal] | null,
): Vector2d | Vector2dDecimal {
  if (typeof pos.x === 'number' || typeof pos.y === 'number') {
    pos = {
      x: Math.round((pos as Vector2d).x / grid) * grid,
      y: Math.round((pos as Vector2d).y / grid) * grid,
    };
  } else {
    pos = {
      x: (pos as Vector2dDecimal).x.div(grid).round().times(grid),
      y: (pos as Vector2dDecimal).y.div(grid).round().times(grid),
    };
  }

  if (!wall) {
    return pos;
  }

  // Find the gridline / wall intersection points, and snap to those when possible.

  // Convert all to Decimal for calculation
  const [w0, w1] = wall as [Vector2dDecimal, Vector2dDecimal];
  const px = pos.x instanceof Decimal ? (pos as Vector2dDecimal).x : new Decimal((pos as Vector2d).x);
  const py = pos.y instanceof Decimal ? (pos as Vector2dDecimal).y : new Decimal((pos as Vector2d).y);

  // Find the closest vertical and horizontal grid lines to the point
  const gridX = px.div(grid).round().times(grid);
  const gridY = py.div(grid).round().times(grid);

  // Helper to get intersection of wall with a vertical or horizontal grid line
  function getIntersectionWithGridLine(
    wallStart: Vector2dDecimal,
    wallEnd: Vector2dDecimal,
    axis: 'x' | 'y',
    value: Decimal,
  ): Vector2dDecimal | null {
    // Wall: (x0, y0) to (x1, y1)
    const x0 = wallStart.x,
      y0 = wallStart.y;
    const x1 = wallEnd.x,
      y1 = wallEnd.y;
    if (axis === 'x') {
      // Vertical line x = value
      if (x1.equals(x0)) return null; // Parallel
      const t = value.minus(x0).div(x1.minus(x0));
      if (t.lessThan(0) || t.greaterThan(1)) return null;
      const y = y0.add(y1.minus(y0).mul(t));
      return { x: value, y };
    } else {
      // Horizontal line y = value
      if (y1.equals(y0)) return null; // Parallel
      const t = value.minus(y0).div(y1.minus(y0));
      if (t.lessThan(0) || t.greaterThan(1)) return null;
      const x = x0.add(x1.minus(x0).mul(t));
      return { x, y: value };
    }
  }

  const candidates: Vector2dDecimal[] = [];
  const ix = getIntersectionWithGridLine(w0, w1, 'x', gridX);
  if (ix) candidates.push(ix);
  const iy = getIntersectionWithGridLine(w0, w1, 'y', gridY);
  if (iy) candidates.push(iy);

  // Pick the intersection closest to the original point
  let snapped: Vector2dDecimal | null = null;
  let minDist = new Decimal(Infinity);
  for (const c of candidates) {
    const dist = c.x.minus(px).pow(2).add(c.y.minus(py).pow(2));
    if (dist.lessThan(minDist)) {
      minDist = dist;
      snapped = c;
    }
  }

  if (snapped) {
    if (typeof pos.x === 'number' || typeof pos.y === 'number') {
      return {
        x: snapped.x.toNumber(),
        y: snapped.y.toNumber(),
      };
    }
    return snapped;
  }

  // Fallback to perpendicular snap

  // Wall vector
  const dx = w1.x.sub(w0.x);
  const dy = w1.y.sub(w0.y);

  // Wall angle
  const wallAngle = Decimal.atan2(dy, dx);

  // Wall normal (perpendicular)
  const nx = Decimal.sin(wallAngle);
  const ny = Decimal.cos(wallAngle).neg();

  // Project point onto wall to get the closest point on the wall
  const wx = w0.x;
  const wy = w0.y;
  const t = dx
    .mul(px.sub(wx))
    .add(dy.mul(py.sub(wy)))
    .div(dx.pow(2).add(dy.pow(2)));
  const projX = wx.add(dx.mul(t));
  const projY = wy.add(dy.mul(t));

  // The vector from the projected point to the original point (perpendicular to wall)
  const perpVecX = px.sub(projX);
  const perpVecY = py.sub(projY);

  // The length along the normal (perpendicular distance)
  const perpDist = perpVecX.mul(nx).add(perpVecY.mul(ny));

  // Snap the perpendicular distance to the grid
  const snappedPerpDist = perpDist.div(grid).round().times(grid);

  // Move from the projected point along the normal by the snapped perpendicular distance
  const snappedX = projX.add(nx.mul(snappedPerpDist));
  const snappedY = projY.add(ny.mul(snappedPerpDist));

  if (typeof pos.x === 'number' || typeof pos.y === 'number') {
    return {
      x: snappedX.toNumber(),
      y: snappedY.toNumber(),
    };
  }
  return {
    x: snappedX,
    y: snappedY,
  };
}

export function snapLengthToGrid<N extends number | Decimal>(length: N, grid: GridSize): N {
  const res = new Decimal(length).div(grid).round().times(grid);
  if (typeof length === 'number') {
    return res.toNumber() as N;
  }
  return res as N;
}

export function isSamePoint(a: Vector2d, b: Vector2d) {
  return Math.abs(a.x - b.x) < 0.00001 && Math.abs(a.y - b.y) < 0.00001;
}

export function isSharedWall(wall: Wall | undefined) {
  return wall ? wall.sectors.length > 1 : false;
}

/**
 * Is this wall lone one, that is it is not connected to a sector.
 * @param wall
 * @returns
 */
export function isLoneWall(wall: Wall) {
  return wall.sectors.length < 1;
}

/**
 * Test if given point is on a line
 * @param p {Vector2d} Point to check
 * @param a {Vector2d} Line start
 * @param b {Vector2d} Line end
 */
export function isPointOnLine(p: Vector2dDecimal, a: Vector2dDecimal, b: Vector2dDecimal): boolean;
export function isPointOnLine(p: Vector2d, a: Vector2d, b: Vector2d): boolean;
export function isPointOnLine(
  p: Vector2d | Vector2dDecimal,
  a: Vector2d | Vector2dDecimal,
  b: Vector2d | Vector2dDecimal,
): boolean {
  if (typeof a.x === 'number' || typeof b.x === 'number' || typeof p.x === 'number') {
    const lineDist = getDistance(a as Vector2d, b as Vector2d);

    const pStartDist = getDistance(p as Vector2d, a as Vector2d);
    const pEndDist = getDistance(p as Vector2d, b as Vector2d);
    return Math.abs(lineDist - (pStartDist + pEndDist)) < 0.001;
  }
  const lineDist = getDistance(a as Vector2dDecimal, b as Vector2dDecimal);

  const pStartDist = getDistance(p as Vector2dDecimal, a as Vector2dDecimal);
  const pEndDist = getDistance(p as Vector2dDecimal, b as Vector2dDecimal);
  return lineDist.sub(pStartDist.add(pEndDist)).abs().lessThan(0.001);
}

export function getDistance(a: Vector2dDecimal, b: Vector2dDecimal): Decimal;
export function getDistance(a: Vector2d, b: Vector2d): number;
export function getDistance(a: Vector2d | Vector2dDecimal, b: Vector2d | Vector2dDecimal): number | Decimal {
  if (typeof a.x === 'number' || typeof a.x === 'number') {
    const an = a as Vector2d;
    const bn = b as Vector2d;
    return Math.sqrt((an.x - bn.x) ** 2 + (an.y - bn.y) ** 2);
  }
  const ad = a as Vector2dDecimal;
  const bd = b as Vector2dDecimal;
  return ad.x.minus(bd.x).pow(2).plus(ad.y.minus(bd.y).pow(2)).sqrt();
}

export function getLineSegmentIntersectionPoint(
  wall1: [Vector2dDecimal, Vector2dDecimal],
  wall2: [Vector2dDecimal, Vector2dDecimal],
): Vector2dDecimal | null {
  const [p1, p2] = wall1;
  const [q1, q2] = wall2;

  // Calculate intersection using parametric equations
  const dx1 = p2.x.sub(p1.x);
  const dy1 = p2.y.sub(p1.y);
  const dx2 = q2.x.sub(q1.x);
  const dy2 = q2.y.sub(q1.y);

  const denom = dx1.mul(dy2).sub(dy1.mul(dx2));
  if (denom.isZero()) {
    // Parallel or coincident
    return null;
  }

  const dx = q1.x.sub(p1.x);
  const dy = q1.y.sub(p1.y);

  const t = dx.mul(dy2).sub(dy.mul(dx2)).div(denom);
  const u = dx.mul(dy1).sub(dy.mul(dx1)).div(denom);

  // t and u must be between 0 and 1 for intersection within segments
  if (t.greaterThanOrEqualTo(0) && t.lessThanOrEqualTo(1) && u.greaterThanOrEqualTo(0) && u.lessThanOrEqualTo(1)) {
    return {
      x: p1.x.add(dx1.mul(t)),
      y: p1.y.add(dy1.mul(t)),
    };
  }

  return null;
}
export function getLineIntersectionPoint(
  wall1: [Vector2dDecimal, Vector2dDecimal],
  wall2: [Vector2dDecimal, Vector2dDecimal],
): Vector2dDecimal | null {
  const [w1Start, w1End] = wall1;
  const [w2Start, w2End] = wall2;

  // Line intersection formula
  const x1 = w1Start.x,
    y1 = w1Start.y;
  const x2 = w1End.x,
    y2 = w1End.y;
  const x3 = w2Start.x,
    y3 = w2Start.y;
  const x4 = w2End.x,
    y4 = w2End.y;

  const denom = x1
    .minus(x2)
    .times(y3.minus(y4))
    .minus(y1.minus(y2).times(x3.minus(x4)));
  if (!denom.isZero()) {
    const px = x1
      .times(y2)
      .minus(y1.times(x2))
      .times(x3.minus(x4))
      .minus(x1.minus(x2).times(x3.times(y4).minus(y3.times(x4))))
      .div(denom);
    const py = x1
      .times(y2)
      .minus(y1.times(x2))
      .times(y3.minus(y4))
      .minus(y1.minus(y2).times(x3.times(y4).minus(y3.times(x4))))
      .div(denom);

    return { x: px, y: py };
  }
  return null;
}
export function isPointInsidePolygon(point: Vector2dDecimal, polygon: Vector2dDecimal[]): boolean;
export function isPointInsidePolygon(point: Vector2d, polygon: Vector2d[]): boolean;
export function isPointInsidePolygon(
  point: Vector2d | Vector2dDecimal,
  polygon: Vector2d[] | Vector2dDecimal[],
): boolean {
  if (typeof point.x === 'number' || typeof point.y === 'number') {
    return _isPointInsidePolygon(point as Vector2d, polygon as Vector2d[]);
  }
  return _isPointInsidePolygon(
    { x: point.x.toNumber(), y: point.y.toNumber() },
    (polygon as Vector2dDecimal[]).map((p) => ({
      x: p.x.toNumber(),
      y: p.y.toNumber(),
    })),
  );
}
function _isPointInsidePolygon(point: Vector2d, polygon: Vector2d[]) {
  // First, check if the point is on any edge
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    if (isPointOnLine(point, polygon[j], polygon[i])) {
      return true;
    }
  }

  // Then, use ray cast to see if the point is actually inside the polygon
  let inside = false;
  const x = point.x;
  const y = point.y;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y;
    const xj = polygon[j].x,
      yj = polygon[j].y;

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + Number.EPSILON) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
}

type WallReference = {
  id: string;
  start: string;
  end: string;
};

type TracedSectorRing = {
  wallIds: string[];
  cornerIds: string[];
};

export function traceSectorWallRings(
  wallIds: string[],
  wallsMap: Record<string, { start: string; end: string }>,
): TracedSectorRing[] {
  const sectorWalls = wallIds
    .map((wallId) => {
      const wall = wallsMap[wallId];
      return wall ? { id: wallId, start: wall.start, end: wall.end } : null;
    })
    .filter((wall): wall is WallReference => Boolean(wall));

  const adjacency: Record<string, Array<{ wallId: string; cornerId: string }>> = {};
  sectorWalls.forEach((wall) => {
    if (!adjacency[wall.start]) adjacency[wall.start] = [];
    if (!adjacency[wall.end]) adjacency[wall.end] = [];
    adjacency[wall.start].push({ wallId: wall.id, cornerId: wall.end });
    adjacency[wall.end].push({ wallId: wall.id, cornerId: wall.start });
  });

  const visitedWalls = new Set<string>();
  const rings: TracedSectorRing[] = [];

  for (const wall of sectorWalls) {
    if (visitedWalls.has(wall.id)) continue;

    const ringWallIds = [wall.id];
    const ringCornerIds = [wall.start, wall.end];
    let currentWallId = wall.id;
    let currentCornerId = wall.end;
    let isClosed = wall.end === wall.start;

    visitedWalls.add(wall.id);

    while (!isClosed) {
      const nextLink = (adjacency[currentCornerId] || []).find(
        ({ wallId }) => wallId !== currentWallId && !visitedWalls.has(wallId),
      );
      if (!nextLink) {
        break;
      }

      ringWallIds.push(nextLink.wallId);
      ringCornerIds.push(nextLink.cornerId);
      visitedWalls.add(nextLink.wallId);
      currentWallId = nextLink.wallId;
      currentCornerId = nextLink.cornerId;
      isClosed = currentCornerId === ringCornerIds[0];
    }

    if (isClosed && ringWallIds.length >= 3) {
      rings.push({
        wallIds: ringWallIds,
        cornerIds: ringCornerIds.slice(0, -1),
      });
    }
  }

  return rings;
}

function getRingCoords(cornerIds: string[], cornersMap: Record<string, { x: number; y: number }>): number[] | null {
  const coords: number[] = [];

  for (const cornerId of cornerIds) {
    const corner = cornersMap[cornerId];
    if (!corner) {
      console.warn('Found a non existing corner!');
      return null;
    }
    coords.push(corner.x, corner.y);
  }

  return coords;
}

function getRingArea(coords: number[]): number {
  let area = 0;

  for (let i = 0; i < coords.length; i += 2) {
    const next = (i + 2) % coords.length;
    area += coords[i] * coords[next + 1] - coords[next] * coords[i + 1];
  }

  return Math.abs(area / 2);
}

/**
 * Traces all polygon rings in a sector by following the wall adjacency graph.
 * Returns each ring as a flat array of [x1, y1, x2, y2, ...] coordinates.
 * A sector with a hole will produce two rings.
 */
export function traceSectorRings(
  wallIds: string[],
  wallsMap: Record<string, { start: string; end: string }>,
  cornersMap: Record<string, { x: number; y: number }>,
): number[][] {
  return traceSectorWallRings(wallIds, wallsMap)
    .map((ring) => getRingCoords(ring.cornerIds, cornersMap))
    .filter((ring): ring is number[] => Boolean(ring));
}

export function getSectorRings(
  wallsMap: Record<string, { start: string; end: string; sectors: string[] }>,
  cornersMap: Record<string, { x: number; y: number }>,
): SectorRings[] {
  const sectorWallIds = new Map<string, string[]>();

  Object.entries(wallsMap).forEach(([wallId, wall]) => {
    wall.sectors.forEach((sectorId) => {
      const existing = sectorWallIds.get(sectorId);
      if (existing) {
        existing.push(wallId);
        return;
      }

      sectorWallIds.set(sectorId, [wallId]);
    });
  });

  return Array.from(sectorWallIds.entries())
    .map(([sectorId, wallIds]) => {
      const tracedRings = traceSectorWallRings(wallIds, wallsMap)
        .map((ring) => {
          const coords = getRingCoords(ring.cornerIds, cornersMap);
          if (!coords) {
            return null;
          }

          return {
            wallIds: ring.wallIds,
            area: getRingArea(coords),
          };
        })
        .filter((ring): ring is { wallIds: string[]; area: number } => Boolean(ring));

      if (tracedRings.length < 1) {
        return null;
      }

      const [outsideRing, ...innerRings] = [...tracedRings].sort((a, b) => b.area - a.area);

      return {
        sectorId,
        outsideRing: outsideRing.wallIds,
        innerRings: innerRings.map((ring) => ring.wallIds),
      };
    })
    .filter((sector): sector is SectorRings => Boolean(sector));
}

export function isInsideSectorRings(point: Vector2d, rings: number[][]): boolean {
  let count = 0;

  for (const ring of rings) {
    const polygon: Vector2d[] = [];
    for (let i = 0; i < ring.length; i += 2) {
      polygon.push({ x: ring[i], y: ring[i + 1] });
    }

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      if (isPointOnLine(point, polygon[j], polygon[i])) {
        return true;
      }
    }

    if (_isPointInsidePolygon(point, polygon)) {
      count++;
    }
  }

  return count % 2 === 1;
}

/**
 * Returns true if the given point is inside a sector, correctly handling holes.
 * Uses an even-odd count across all rings: a point inside an odd number of rings is inside the sector.
 */
export function isInsideSector(
  point: Vector2d,
  wallIds: string[],
  wallsMap: Record<string, { start: string; end: string }>,
  cornersMap: Record<string, { x: number; y: number }>,
): boolean {
  const rings = traceSectorRings(wallIds, wallsMap, cornersMap);
  return isInsideSectorRings(point, rings);
}

export function areSegmentsCollinearAndAdjacent(
  seg1: [Vector2dDecimal, Vector2dDecimal],
  seg2: [Vector2dDecimal, Vector2dDecimal],
  tolerance = new Decimal('0.1'),
): boolean {
  const [a1, a2] = seg1;
  const [b1, b2] = seg2;

  // Check if all points are collinear (cross product == 0)
  const dx1 = a2.x.minus(a1.x);
  const dy1 = a2.y.minus(a1.y);
  const dx2 = b2.x.minus(b1.x);
  const dy2 = b2.y.minus(b1.y);

  // Vectors are parallel if cross product is zero
  const cross = dx1.mul(dy2).minus(dy1.mul(dx2));
  if (!cross.abs().lessThan(tolerance)) return false;

  // Check if projections overlap
  const isBetween = (v: Vector2dDecimal, w1: Vector2dDecimal, w2: Vector2dDecimal) =>
    v.x.greaterThanOrEqualTo(Decimal.min(w1.x, w2.x).minus(tolerance)) &&
    v.x.lessThanOrEqualTo(Decimal.max(w1.x, w2.x).plus(tolerance)) &&
    v.y.greaterThanOrEqualTo(Decimal.min(w1.y, w2.y).minus(tolerance)) &&
    v.y.lessThanOrEqualTo(Decimal.max(w1.y, w2.y).plus(tolerance));

  return isBetween(a1, b1, b2) || isBetween(a2, b1, b2) || isBetween(b1, a1, a2) || isBetween(b2, a1, a2);
}

/**
 * True when two segments cross each other or run collinear on top of each other.
 */
export function doSegmentsOverlap(seg1: [Vector2d, Vector2d], seg2: [Vector2d, Vector2d]): boolean {
  const dec1: [Vector2dDecimal, Vector2dDecimal] = [convertNumVectorToDec(seg1[0]), convertNumVectorToDec(seg1[1])];
  const dec2: [Vector2dDecimal, Vector2dDecimal] = [convertNumVectorToDec(seg2[0]), convertNumVectorToDec(seg2[1])];

  if (getLineSegmentIntersectionPoint(dec1, dec2)) {
    return true;
  }

  return areSegmentsCollinearAndAdjacent(dec1, dec2);
}

export function calcRectRoomSize(
  corners: Record<string, Corner>,
  height: Measurement<'length'>,
  units: 'si' | 'us',
): { w: number; l: number; h: number; a: number } {
  const roomCorners = Object.values(corners);

  if (roomCorners.length < 2) {
    throw new InvalidStateError('Can not calculate rectangular room size without at least two corners');
  }

  const xValues = roomCorners.map((corner) => corner.position.x.value);
  const yValues = roomCorners.map((corner) => corner.position.y.value);

  const targetUnit: LengthUnit = units === 'si' ? 'm' : 'ft';

  const width = convertMeasurement(
    createMeasurement(Math.max(...xValues) - Math.min(...xValues), 'mm'),
    targetUnit,
  ).value;
  const length = convertMeasurement(
    createMeasurement(Math.max(...yValues) - Math.min(...yValues), 'mm'),
    targetUnit,
  ).value;
  const roomHeight = convertMeasurement(height, targetUnit).value;

  return {
    w: width,
    l: length,
    h: roomHeight,
    a: new Decimal(width).times(length).toDecimalPlaces(6).toNumber(),
  };
}

export function createRectRoom(
  la: number,
  wa: number,
  ha: number,
  gridUnits: 'si' | 'us',
): { walls: Record<string, Wall>; corners: Record<string, Corner>; sectors: Record<string, Sector> } {
  const gridGuideDistance = gridUnits === 'si' ? 1000 : 25.4 * 12;

  // Try to find a nice corner position on the thicker grid (every 1m)
  // Preferably somewhat centered on the origin

  const startX = new Decimal(wa).div(2).div(gridGuideDistance).round().times(-gridGuideDistance);
  const startY = new Decimal(la).div(2).div(gridGuideDistance).round().times(-gridGuideDistance);

  const height = {
    value: ha,
    unit: 'mm',
    type: 'length' as const,
  };

  // Generate Corners, Walls and a Sector
  /**      w1
   *    1 ---- 2
   * w4 |      | w2
   *    |      |
   *    4 ---- 3
   *       w3
   */
  const corners: Record<string, Corner> = {};
  const c1: Corner = {
    id: generateRoomPartId(),
    position: {
      x: {
        value: startX.toDecimalPlaces(6).toNumber(),
        unit: 'mm',
        type: 'length',
      },
      y: {
        value: startY.toDecimalPlaces(6).toNumber(),
        unit: 'mm',
        type: 'length',
      },
      z: {
        value: 0,
        unit: 'mm',
        type: 'length',
      },
    },
    height,
  };
  const c2: Corner = {
    id: generateRoomPartId(),
    position: {
      x: {
        value: startX.add(wa).toDecimalPlaces(6).toNumber(),
        unit: 'mm',
        type: 'length',
      },
      y: {
        value: startY.toDecimalPlaces(6).toNumber(),
        unit: 'mm',
        type: 'length',
      },
      z: {
        value: 0,
        unit: 'mm',
        type: 'length',
      },
    },
    height,
  };
  const c3: Corner = {
    id: generateRoomPartId(),
    position: {
      x: {
        value: startX.add(wa).toDecimalPlaces(6).toNumber(),
        unit: 'mm',
        type: 'length',
      },
      y: {
        value: startY.add(la).toDecimalPlaces(6).toNumber(),
        unit: 'mm',
        type: 'length',
      },
      z: {
        value: 0,
        unit: 'mm',
        type: 'length',
      },
    },
    height,
  };
  const c4: Corner = {
    id: generateRoomPartId(),
    position: {
      x: {
        value: startX.toDecimalPlaces(6).toNumber(),
        unit: 'mm',
        type: 'length',
      },
      y: {
        value: startY.add(la).toDecimalPlaces(6).toNumber(),
        unit: 'mm',
        type: 'length',
      },
      z: {
        value: 0,
        unit: 'mm',
        type: 'length',
      },
    },
    height,
  };
  corners[c1.id] = c1;
  corners[c2.id] = c2;
  corners[c3.id] = c3;
  corners[c4.id] = c4;

  const sectorId = generateRoomPartId();

  const w1: Wall = {
    id: generateRoomPartId(),
    start: c1.id,
    end: c2.id,
    sectors: [sectorId],
  };
  const w2: Wall = {
    id: generateRoomPartId(),
    start: c2.id,
    end: c3.id,
    sectors: [sectorId],
  };
  const w3: Wall = {
    id: generateRoomPartId(),
    start: c3.id,
    end: c4.id,
    sectors: [sectorId],
  };
  const w4: Wall = {
    id: generateRoomPartId(),
    start: c4.id,
    end: c1.id,
    sectors: [sectorId],
  };
  const walls: Record<string, Wall> = {
    [w1.id]: w1,
    [w2.id]: w2,
    [w3.id]: w3,
    [w4.id]: w4,
  };

  const roomData = {
    corners,
    walls: walls,
    sectors: {
      [sectorId]: {
        id: sectorId,
        walls: [w1.id, w2.id, w3.id, w4.id],
      },
    },
  };

  return roomData;
}

/**
 * This seems to have issues when one of x or y distance is 0 but the other is way out
 * @param ls
 * @param le
 * @param point
 * @returns
 */
export function getLinePointDistance(ls: Vector2dDecimal, le: Vector2dDecimal, point: Vector2dDecimal) {
  const A = le.y.minus(ls.y).times(point.x);
  const B = le.x.minus(ls.x).times(point.y);
  const C = le.x.times(ls.y).minus(le.y.times(ls.x));

  const numerator = A.minus(B).add(C).abs();

  const denominator = le.y.minus(ls.y).pow(2).add(le.x.minus(ls.x).pow(2)).sqrt();

  const dist = numerator.div(denominator);

  return dist;
}

export function getNearestPointOnLine(
  ls: Vector2dDecimal,
  le: Vector2dDecimal,
  point: Vector2dDecimal,
): Vector2dDecimal {
  const lsToLe = { x: le.x.minus(ls.x), y: le.y.minus(ls.y) };
  const lsToPoint = { x: point.x.minus(ls.x), y: point.y.minus(ls.y) };
  const len = lsToLe.x.times(lsToLe.x).add(lsToLe.y.times(lsToLe.y));
  let dot = lsToPoint.x.times(lsToLe.x).add(lsToPoint.y.times(lsToLe.y));
  const t = dot.div(len).clamp(0, 1);

  dot = le.x
    .minus(ls.x)
    .times(point.y.minus(ls.y))
    .minus(le.y.minus(ls.y).times(point.x.minus(ls.x)));

  const res = {
    x: ls.x.add(lsToLe.x.times(t)),
    y: ls.y.add(lsToLe.y.times(t)),
  };

  return res;
}

export function convertNumVectorToDec(vec: Vector2d): Vector2dDecimal {
  return {
    x: new Decimal(vec.x),
    y: new Decimal(vec.y),
  };
}
export function convertDecVectorToNum(vec: Vector2dDecimal): Vector2d {
  return {
    x: vec.x.toNumber(),
    y: vec.y.toNumber(),
  };
}

export function getDiff(v1: Vector2dDecimal, v2: Vector2dDecimal): Vector2dDecimal {
  return {
    x: v2.x.sub(v1.x),
    y: v2.y.sub(v1.y),
  };
}

export function addVectors(v1: Vector2dDecimal, v2: Vector2dDecimal): Vector2dDecimal {
  return {
    x: v1.x.add(v2.x),
    y: v1.y.add(v2.y),
  };
}

export function printVec(v: Vector2d | Vector2dDecimal) {
  if (typeof v.x === 'number' || typeof v.y === 'number') {
    return `(${v.x}, ${v.y})`;
  }
  return `(${v.x.toDecimalPlaces(4).toString()}, ${v.y.toDecimalPlaces(4).toString()})`;
}

export function convertVector<T extends Vector2dDecimal | Vector2d>(
  vec: T,
): T extends Vector2d ? Vector2dDecimal : Vector2d {
  if (typeof vec.x === 'number' || typeof vec.y === 'number') {
    return {
      x: new Decimal(vec.x),
      y: new Decimal(vec.y),
    } as T extends Vector2d ? Vector2dDecimal : Vector2d;
  } else {
    return {
      x: (vec.x as Decimal).toNumber(),
      y: (vec.y as Decimal).toNumber(),
    } as T extends Vector2d ? Vector2dDecimal : Vector2d;
  }
}

export function addDecVectors(v1: Vector2dDecimal, v2: Vector2dDecimal): Vector2dDecimal {
  return {
    x: v1.x.add(v2.x),
    y: v1.y.add(v2.y),
  };
}

export function subDecVectors(v1: Vector2dDecimal, v2: Vector2dDecimal): Vector2dDecimal {
  return {
    x: v1.x.sub(v2.x),
    y: v1.y.sub(v2.y),
  };
}

export function scaleAssetForPlanner<T extends DeviceLike>(asset: T): T {
  if (!asset.airchefValues.transform) {
    console.warn(`Asset of type "${asset.type}" has no transform set`);
    return asset;
  }

  const clone = simpleClone(asset);

  if (clone.airchefValues.transform) {
    clone.airchefValues.transform.position.x = convertMeasurement(clone.airchefValues.transform.position.x, 'cm');
    clone.airchefValues.transform.position.y = convertMeasurement(clone.airchefValues.transform.position.y, 'cm');
    clone.airchefValues.transform.position.z = convertMeasurement(clone.airchefValues.transform.position.z, 'cm');
  }

  if (clone.airchefValues.transform?.size) {
    clone.airchefValues.transform.size.height = convertMeasurement(clone.airchefValues.transform.size.height, 'cm');
    clone.airchefValues.transform.size.width = convertMeasurement(clone.airchefValues.transform.size.width, 'cm');
    clone.airchefValues.transform.size.length = convertMeasurement(clone.airchefValues.transform.size.length, 'cm');
  }

  if (clone.type === 'hood' && clone.haltonValues) {
    clone.haltonValues.maxSize.height = convertMeasurement(clone.haltonValues.maxSize.height, 'cm');
    clone.haltonValues.maxSize.width = convertMeasurement(clone.haltonValues.maxSize.width, 'cm');
    clone.haltonValues.maxSize.length = convertMeasurement(clone.haltonValues.maxSize.length, 'cm');

    clone.haltonValues.minSize.height = convertMeasurement(clone.haltonValues.minSize.height, 'cm');
    clone.haltonValues.minSize.width = convertMeasurement(clone.haltonValues.minSize.width, 'cm');
    clone.haltonValues.minSize.length = convertMeasurement(clone.haltonValues.minSize.length, 'cm');
  }

  if (clone.airchefValues.offsetTransform) {
    clone.airchefValues.offsetTransform.position.x = convertMeasurement(
      clone.airchefValues.offsetTransform.position.x,
      'cm',
    );
    clone.airchefValues.offsetTransform.position.y = convertMeasurement(
      clone.airchefValues.offsetTransform.position.y,
      'cm',
    );
    clone.airchefValues.offsetTransform.position.z = convertMeasurement(
      clone.airchefValues.offsetTransform.position.z,
      'cm',
    );

    clone.airchefValues.offsetTransform.size.height = convertMeasurement(
      clone.airchefValues.offsetTransform.size.height,
      'cm',
    );
    clone.airchefValues.offsetTransform.size.width = convertMeasurement(
      clone.airchefValues.offsetTransform.size.width,
      'cm',
    );
    clone.airchefValues.offsetTransform.size.length = convertMeasurement(
      clone.airchefValues.offsetTransform.size.length,
      'cm',
    );
  }

  return clone;
}

export function scaleAssetForStore<T extends DeviceLike>(asset: T): T {
  if (!asset.airchefValues.transform) {
    console.warn('Asset has no transform set');
    return asset;
  }

  const clone = simpleClone(asset);

  clone.airchefValues.transform.position.x = convertMeasurement(clone.airchefValues.transform.position.x, 'mm');
  clone.airchefValues.transform.position.y = convertMeasurement(clone.airchefValues.transform.position.y, 'mm');
  clone.airchefValues.transform.position.z = convertMeasurement(clone.airchefValues.transform.position.z, 'mm');

  if (clone.airchefValues.transform.size) {
    clone.airchefValues.transform.size.height = convertMeasurement(clone.airchefValues.transform.size.height, 'mm');
    clone.airchefValues.transform.size.width = convertMeasurement(clone.airchefValues.transform.size.width, 'mm');
    clone.airchefValues.transform.size.length = convertMeasurement(clone.airchefValues.transform.size.length, 'mm');
  }

  if (clone.airchefValues.offsetTransform) {
    clone.airchefValues.offsetTransform.position.x = convertMeasurement(
      clone.airchefValues.offsetTransform.position.x,
      'mm',
    );
    clone.airchefValues.offsetTransform.position.y = convertMeasurement(
      clone.airchefValues.offsetTransform.position.y,
      'mm',
    );
    clone.airchefValues.offsetTransform.position.z = convertMeasurement(
      clone.airchefValues.offsetTransform.position.z,
      'mm',
    );

    clone.airchefValues.offsetTransform.size.height = convertMeasurement(
      clone.airchefValues.offsetTransform.size.height,
      'mm',
    );
    clone.airchefValues.offsetTransform.size.width = convertMeasurement(
      clone.airchefValues.offsetTransform.size.width,
      'mm',
    );
    clone.airchefValues.offsetTransform.size.length = convertMeasurement(
      clone.airchefValues.offsetTransform.size.length,
      'mm',
    );
  }

  return clone;
}

export function convertMeasurementToCm(measurement: Measurement<'length'>, toDec?: false): number;
export function convertMeasurementToCm(measurement: Measurement<'length'>, toDec: true): Decimal;
export function convertMeasurementToCm(measurement: Measurement<'length'>, toDec?: boolean): Decimal | number;
export function convertMeasurementToCm(measurement: Measurement<'length'>, toDec: boolean = false): number | Decimal {
  if (measurement.type !== undefined && measurement.type !== 'length') {
    throw new InvalidStateError('Trying to convert a non-length measurement to cm!');
  }
  const val = new Decimal(measurement.value);
  switch (measurement.unit) {
    case 'cm':
      return toDec ? val : measurement.value;
    case 'mm':
      return toDec ? val.times(0.1) : val.times(0.1).toNumber();
    case 'dm':
      return toDec ? val.times(10) : val.times(10).toNumber();
    case 'm':
      return toDec ? val.times(100) : val.times(100).toNumber();
    case 'ft':
      return toDec ? val.times(30.48) : val.times(30.48).toNumber();
    case 'in':
      return toDec ? val.times(2.54) : val.times(2.54).toNumber();
    default:
      throw new InvalidStateError(`Unknown unit to convert to cm! (${measurement.unit})`);
  }
}

export function convertCmToMeasurement(value: number, unit: LengthUnit = 'mm'): Measurement<'length'> {
  const val = new Decimal(value);
  let newValue = value;
  switch (unit) {
    case 'cm':
      newValue = value;
      break;
    case 'mm':
      newValue = val.times(10).toNumber();
      break;
    case 'dm':
      newValue = val.times(0.1).toNumber();
      break;
    case 'm':
      newValue = val.times(0.01).toNumber();
      break;
    case 'ft':
      newValue = val.times(0.0328084).toNumber();
      break;
    case 'in':
      newValue = val.times(0.3937008).toNumber();
      break;
    default:
      throw new InvalidStateError(`Unknown unit to convert to cm! (${unit})`);
  }

  return {
    value: newValue,
    unit,
    type: 'length',
  };
}

export function tryGetMeasurementValue<T extends MeasurementType>(
  measurement: Measurement<T> | undefined,
  targetUnit: UnitOfMeasurement<T>,
): number {
  if (!measurement) {
    return 0;
  }

  return convertMeasurement(measurement, targetUnit).value;
}

export function createPosition(x: number, y: number, z: number = 0, unit: LengthUnit = 'cm'): Position {
  return {
    x: {
      value: x,
      unit,
      type: 'length',
    },
    y: {
      value: y,
      unit,
      type: 'length',
    },
    z: {
      value: z,
      unit,
      type: 'length',
    },
  };
}

export const PI = Decimal.acos(-1);

const zeroPoint: Vector2d = { x: 0, y: 0 };
const zeroPointDec: Vector2dDecimal = { x: new Decimal(0), y: new Decimal(0) };
export function getDeviceCorners(
  device: DeviceLike,
  overridePos?: Vector2dDecimal | Vector2d,
  overrideAngle?: number | Decimal,
  inDecimal?: false,
): FourCorners;
export function getDeviceCorners(
  device: DeviceLike,
  overridePos?: Vector2dDecimal | Vector2d,
  overrideAngle?: number | Decimal,
  inDecimal?: true,
): FourCornersDec;
export function getDeviceCorners(
  device: DeviceLike,
  overridePos?: Vector2dDecimal | Vector2d,
  overrideAngle?: number | Decimal,
  inDecimal?: boolean,
): FourCorners | FourCornersDec {
  if (
    device?.airchefValues?.transform?.position?.x?.value === undefined ||
    device?.airchefValues?.transform?.position?.y?.value === undefined
  ) {
    return inDecimal
      ? [{ ...zeroPointDec }, { ...zeroPointDec }, { ...zeroPointDec }, { ...zeroPointDec }]
      : [{ ...zeroPoint }, { ...zeroPoint }, { ...zeroPoint }, { ...zeroPoint }];
  }
  // Calculate each corner point of the asset, based on size & rotation
  const angle = new Decimal(overrideAngle ?? device.airchefValues.transform.rotation.x).times(PI).div(180);
  const width = new Decimal(
    device.airchefValues.offsetTransform?.size.length.value ?? device.airchefValues.transform.size.length.value,
  );
  const height = new Decimal(
    device.airchefValues.offsetTransform?.size.width.value ?? device.airchefValues.transform.size.width.value,
  );

  const zero = new Decimal(0);

  let x = new Decimal(
    device.airchefValues.offsetTransform?.position.x.value ?? device.airchefValues.transform.position.x.value,
  );
  let y = new Decimal(
    device.airchefValues.offsetTransform?.position.y.value ?? device.airchefValues.transform.position.y.value,
  );

  if (overridePos !== undefined) {
    if (typeof overridePos.x === 'number' || typeof overridePos.y === 'number') {
      x = new Decimal(overridePos.x);
      y = new Decimal(overridePos.y);
    } else {
      x = overridePos.x;
      y = overridePos.y;
    }
  }

  // top right
  const trX = width.times(angle.cos()).minus(zero.times(angle.sin())).add(x);
  const trY = width.times(angle.sin()).add(zero.times(angle.cos())).add(y);

  // bottom right
  const brX = width.times(angle.cos()).minus(height.times(angle.sin())).add(x);
  const brY = width.times(angle.sin()).add(height.times(angle.cos())).add(y);

  // bottom left
  const blX = zero.times(angle.cos()).minus(height.times(angle.sin())).add(x);
  const blY = zero.times(angle.sin()).add(height.times(angle.cos())).add(y);

  return [
    // top left
    {
      x: inDecimal ? x : x.toNumber(),
      y: inDecimal ? y : y.toNumber(),
    },
    // top right
    {
      x: inDecimal ? trX : trX.toNumber(),
      y: inDecimal ? trY : trY.toNumber(),
    },
    // bottom right
    {
      x: inDecimal ? brX : brX.toNumber(),
      y: inDecimal ? brY : brY.toNumber(),
    },
    // bottom left
    {
      x: inDecimal ? blX : blX.toNumber(),
      y: inDecimal ? blY : blY.toNumber(),
    },
  ] as FourCorners | FourCornersDec;
}

/**
 * Expects the source device to be already in the drawing units, cm
 * @param device
 * @param groupPos
 * @param devicePos
 * @param rotation
 */
export function applyOffsetTransforms<T extends DeviceLike>(
  device: T,
  groupPos: Vector2d,
  devicePos: Vector2d,
  rotation: number,
): T {
  const _groupPos = convertVector(groupPos);
  const _devicePos = convertVector(devicePos);
  const _device = simpleClone(device);
  const _rotation = new Decimal(rotation);

  _device.airchefValues.transform.position.x.value = _devicePos.x.toDecimalPlaces(6).toNumber();
  _device.airchefValues.transform.position.y.value = _devicePos.y.toDecimalPlaces(6).toNumber();
  _device.airchefValues.transform.rotation.x = _rotation.toDecimalPlaces(6).toNumber();

  // Halton length - 2D x dimension
  const fullLength = _device.airchefValues.positionOffset?.x
    ? measurementCalc.add(
        measurementCalc.abs(_device.airchefValues.positionOffset.x),
        _device.airchefValues.transform.size.length,
        'cm',
        6,
      )
    : _device.airchefValues.transform.size.length;

  // Halton width - 2D y dimension
  const fullWidth = _device.airchefValues.positionOffset?.y
    ? measurementCalc.add(
        measurementCalc.abs(_device.airchefValues.positionOffset.y),
        _device.airchefValues.transform.size.width,
        'cm',
        6,
      )
    : _device.airchefValues.transform.size.width;

  // Device height, no offsets used / supported
  const fullHeight = _device.airchefValues.transform.size.height;

  const offsetTransform: OffsetTransformProperties = {
    size: {
      width: fullWidth,
      length: fullLength,
      height: fullHeight,
    },
    position: {
      x: createMeasurement(_groupPos.x.toDecimalPlaces(6).toNumber(), 'cm', 'length'),
      y: createMeasurement(_groupPos.y.toDecimalPlaces(6).toNumber(), 'cm', 'length'),
      z: _device.airchefValues.transform.position.z,
    },
  };

  _device.airchefValues.offsetTransform = offsetTransform;

  return _device;
}
